/**
 * Hostile-input handling for the Foundry mappers (2.G deliverable 2, rule 9). `cloneBounded`
 * copies an untyped actor into plain JSON, refusing one that is too deep or too large and
 * dropping the keys that could reach a prototype; the mappers read only the copy, through the
 * total readers below, so nothing they do can throw or pollute an object.
 */
export const INPUT_LIMITS = Object.freeze({
    depth: 64,
    nodes: 1_000_000,
    items: 5_000,
    stringLength: 4 * 1024 * 1024,
});
const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
/** A plain-JSON copy of `value`; non-JSON values become null, forbidden keys are dropped. */
export function cloneBounded(value) {
    let nodes = 0;
    let failure = null;
    const copy = (v, depth) => {
        if (failure !== null)
            return null;
        nodes += 1;
        if (nodes > INPUT_LIMITS.nodes)
            failure = 'too_large';
        if (depth > INPUT_LIMITS.depth)
            failure = 'too_deep';
        if (failure !== null)
            return null;
        if (typeof v === 'string') {
            if (v.length > INPUT_LIMITS.stringLength)
                failure = 'too_large';
            return v;
        }
        if (typeof v === 'number')
            return Number.isFinite(v) ? v : null;
        if (typeof v === 'boolean' || v === null)
            return v;
        if (Array.isArray(v)) {
            // A sparse array's length is not its size: refuse a length past the node limit unwalked.
            if (v.length > INPUT_LIMITS.nodes)
                failure = 'too_large';
            if (failure !== null)
                return null;
            // Array.from, not map: a hole becomes null instead of staying a hole.
            return Array.from(v, (e) => copy(e, depth + 1));
        }
        if (typeof v !== 'object')
            return null;
        const out = {};
        for (const key of Object.keys(v)) {
            if (FORBIDDEN_KEYS.has(key))
                continue;
            const inner = v[key];
            if (inner === undefined || typeof inner === 'function' || typeof inner === 'symbol')
                continue;
            out[key] = copy(inner, depth + 1);
        }
        return out;
    };
    const value_ = copy(value, 0);
    return failure === null ? { ok: true, value: value_ } : { ok: false, reason: failure };
}
const EMPTY = Object.freeze({});
export function isRec(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/** The object at `v`, or an empty one. */
export function rec(v) {
    return isRec(v) ? v : EMPTY;
}
/** The value at a dotted path of plain keys, or undefined. */
export function at(v, path) {
    let cur = v;
    for (const key of path.split('.')) {
        if (!isRec(cur) || !Object.hasOwn(cur, key))
            return undefined;
        cur = cur[key];
    }
    return cur;
}
export function arr(v) {
    return Array.isArray(v) ? v : [];
}
/** A string, a number written as a string, or `fallback`. */
export function str(v, fallback) {
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number')
        return String(v);
    return fallback;
}
/** A finite number, a numeric string as a number, or null. */
export function num(v) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
/** An integer clamped into [min, max], or null when there is no number. */
export function int(v, min = -1e9, max = 1e9) {
    const n = num(v);
    return n === null ? null : Math.min(max, Math.max(min, Math.trunc(n)));
}
export function bool(v) {
    return typeof v === 'boolean' ? v : null;
}
export function strings(v) {
    return arr(v).filter((s) => typeof s === 'string' && s !== '');
}
const FOUNDRY_ID = /^[A-Za-z0-9]{16}$/;
export function foundryId(v) {
    return typeof v === 'string' && FOUNDRY_ID.test(v) ? v : null;
}
//# sourceMappingURL=input.js.map